import { ReactComponent as CalendarIcon } from "components/Icons/calendar.svg";
import { ReactComponent as CodeSquare01Icon } from "components/Icons/code-square-01.svg";
import { ReactComponent as CurrencyDollarCircleIcon } from "components/Icons/currency-dollar-circle.svg";
import { ReactComponent as Grid02Icon } from "components/Icons/grid-02.svg";
import { ReactComponent as RefreshCW05Icon } from "components/Icons/refresh-cw-05.svg";
import { ReactComponent as ShoppingCart01Icon } from "components/Icons/shopping-cart-01.svg";
import { ReactComponent as User01Icon } from "components/Icons/user-01.svg";
import { ReactComponent as Users01Icon } from "components/Icons/users-01.svg";
import { ReactComponent as ZapIcon } from "components/Icons/zap.svg";
import { Descriptor } from "hooks/intl";
import { isEmpty, isUndefined } from "lodash";
import {
  AchievementAggregation,
  AchievementCompleteRulePeriod,
  AchievementRuleTypes,
  AchievementTriggers,
  CompleteRule,
  FEAchievementRule,
} from "types/achievements";

export const getTypeAndTriggerBadgesDetails = (
  ruleData: FEAchievementRule,
  eventName: string,
  message: (descriptor: Descriptor, values?: any, opts?: any) => string,
) => {
  const badges = [];

  const { DIRECT, REFERRAL } = AchievementRuleTypes;
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;

  switch (ruleData.type) {
    case DIRECT:
      badges.push({
        icon: User01Icon,
        label: message({ id: "directRuleType" }),
      });
      break;
    case REFERRAL:
      badges.push({
        icon: Users01Icon,
        label: message({ id: "referralRuleType" }),
      });
      break;
  }

  switch (ruleData.trigger) {
    case TRANSACTION:
      badges.push({
        icon: ShoppingCart01Icon,
        label: message({ id: "purchaseTransaction" }),
      });
      break;
    case CUSTOM_EVENT:
      badges.push({
        icon: CodeSquare01Icon,
        label: message({ id: "customEventWithSchema" }, { schema: eventName }),
      });
      break;
  }

  return badges.length > 0 ? badges : undefined;
};

export const getProgressTrackingBadgeDetails = (
  ruleData: FEAchievementRule,
  message: (descriptor: Descriptor, values?: any, opts?: any) => string,
) => {
  const aggregationType = ruleData.aggregation.type;
  const trigger = ruleData.trigger;

  const { QUANTITY, EXPRESSION } = AchievementAggregation;
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;

  let badgeDetails;

  switch (true) {
    case trigger === TRANSACTION && aggregationType === QUANTITY:
      badgeDetails = {
        icon: ShoppingCart01Icon,
        label: message({ id: "numberOfTransactions" }),
      };
      break;
    case trigger === TRANSACTION && aggregationType === EXPRESSION:
      badgeDetails = {
        icon: CurrencyDollarCircleIcon,
        label: message({ id: "valueOfTransactions" }),
      };
      break;
    case trigger === CUSTOM_EVENT && aggregationType === QUANTITY:
      badgeDetails = {
        icon: CodeSquare01Icon,
        label: message({ id: "numberOfCustomEvents" }),
      };
      break;
    case trigger === CUSTOM_EVENT && aggregationType === EXPRESSION:
      badgeDetails = {
        icon: Grid02Icon,
        label: message({ id: "valueOfCustomEvents" }),
      };
      break;
  }

  return !!badgeDetails ? [badgeDetails] : undefined;
};

export const getGoalBadgeDetails = (
  completeRule: CompleteRule | undefined,
  message: (descriptor: Descriptor, values?: any, opts?: any) => string,
) => {
  const { OVERALL, LAST_DAY, STREAK } = AchievementCompleteRulePeriod;

  if (isEmpty(completeRule) || isUndefined(completeRule.period)) {
    return undefined;
  }

  let badgeDetails;

  switch (completeRule?.period?.type) {
    case OVERALL:
      badgeDetails = {
        icon: RefreshCW05Icon,
        label: message({ id: "overall" }),
      };
      break;
    case LAST_DAY:
      badgeDetails = {
        icon: CalendarIcon,
        label: message({ id: "lastxDays" }),
      };
      break;
    case STREAK:
      badgeDetails = {
        icon: ZapIcon,
        label: message({ id: "streak" }),
      };
      break;
  }

  return !!badgeDetails ? [badgeDetails] : undefined;
};
